/**
 * Media library hooks — list, upload, delete, consent.
 *
 * Upload uses multipart/form-data (no JSON wrapping) so it streams the file
 * bytes without base64 expansion. Per-tier limits are returned by the list
 * endpoint and surfaced as a usage widget in the UI.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

export const MEDIA_KEY = ['admin', 'media'] as const;

export type MediaKind =
  | 'logo'
  | 'clinic_interior'
  | 'equipment'
  | 'team_photo'
  | 'patient_photo'
  | 'other';

export interface MediaAsset {
  id: string;
  kind: MediaKind;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  original_filename: string;
  alt_text: string | null;
  consent_signed_at: number | null;
  consent_subject_kind: 'doctor' | 'patient' | null;
  consent_subject_id: string | null;
  linked_doctor_id: string | null;
  linked_service_slug: string | null;
  created_at: number;
  public_url: string;
}

export interface MediaUsage {
  items: number;
  bytes: number;
  mb: number;
  limit_items: number;
  limit_mb: number;
}

async function mediaFetch(path: string, init: RequestInit = {}) {
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  // Don't set Content-Type when body is FormData — browser must set its
  // boundary parameter automatically.
  if (!(init.body instanceof FormData)) headers['content-type'] ??= 'application/json';
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
}

export function useMedia() {
  return useQuery({
    queryKey: MEDIA_KEY,
    queryFn: async (): Promise<{ assets: MediaAsset[]; usage: MediaUsage }> => {
      const res = await mediaFetch('/api/admin/media');
      if (!res.ok) throw new Error('failed_to_load_media');
      return (await res.json()) as { assets: MediaAsset[]; usage: MediaUsage };
    },
    staleTime: 30_000,
  });
}

export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      kind: MediaKind;
      alt_text?: string;
      linked_doctor_id?: string;
      linked_service_slug?: string;
    }) => {
      const form = new FormData();
      form.set('file', input.file);
      form.set('kind', input.kind);
      if (input.alt_text) form.set('alt_text', input.alt_text);
      if (input.linked_doctor_id) form.set('linked_doctor_id', input.linked_doctor_id);
      if (input.linked_service_slug) form.set('linked_service_slug', input.linked_service_slug);
      // Best-effort client-side dimension extraction.
      const dims = await imageDimensions(input.file).catch(() => null);
      if (dims) {
        form.set('width', String(dims.w));
        form.set('height', String(dims.h));
      }
      const res = await mediaFetch('/api/admin/media/upload', { method: 'POST', body: form });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string; limit?: number; limit_mb?: number };
        throw new Error(err.error ?? 'upload_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_KEY }),
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await mediaFetch(`/api/admin/media/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_KEY }),
  });
}

export function usePatchMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      alt_text?: string;
      kind?: MediaKind;
      linked_doctor_id?: string | null;
      linked_service_slug?: string | null;
    }) => {
      const { id, ...rest } = input;
      const res = await mediaFetch(`/api/admin/media/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(rest),
      });
      if (!res.ok) throw new Error('patch_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_KEY }),
  });
}

export function useMarkConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      subject_kind?: 'doctor' | 'patient';
      subject_id?: string;
      consent_email?: string;
    }) => {
      const { id, ...rest } = input;
      const res = await mediaFetch(`/api/admin/media/${id}/consent`, {
        method: 'POST',
        body: JSON.stringify(rest),
      });
      if (!res.ok) throw new Error('consent_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_KEY }),
  });
}

function imageDimensions(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      URL.revokeObjectURL(url);
      resolve({ w, h });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('not_an_image'));
    };
    img.src = url;
  });
}

/**
 * Cloudflare Image Transformation URL. Same-origin source — the routes-worker
 * serves /media/<id>/<filename> from R2 and the zone transforms via
 * /cdn-cgi/image/<opts>/<source>. When transforms aren't available (local
 * dev, zone without Polish enabled), the raw URL still works.
 */
export function transformedUrl(
  publicUrl: string,
  opts: { width?: number; height?: number; fit?: 'cover' | 'contain' | 'scale-down'; quality?: number } = {}
): string {
  const params: string[] = ['format=auto'];
  if (opts.width) params.push(`width=${opts.width}`);
  if (opts.height) params.push(`height=${opts.height}`);
  if (opts.fit) params.push(`fit=${opts.fit}`);
  if (opts.quality) params.push(`quality=${opts.quality}`);
  return `/cdn-cgi/image/${params.join(',')}${publicUrl}`;
}
